import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import * as React from 'react';
import { useMemo } from 'react';

import Loader from '../../Loader/Loader';

export interface ITableColumn<T> {
  label: string;
  render: (item: T) => React.ReactNode;
}

interface ITableProps<T> {
  data?: T[] | null;
  columns: ITableColumn<T>[];
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  isLoading: boolean;
  perPageOptions?: (number | { value: number; label: string })[];
}

export const TableMUI = <T, B>({
  data,
  columns,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  isLoading,
  perPageOptions,
}: ITableProps<T>) => {
  const theme = useTheme();

  const placeholder = useMemo(
    () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 400,
        }}
      >
        <Typography fontWeight={theme.typography.fontWeightMedium}>No confirmed sends yet</Typography>
        <Typography sx={{ fontSize: 14 }}>Waiting for information</Typography>
      </Box>
    ),
    [theme.typography.fontWeightMedium],
  );

  return (
    <Box>
      {isLoading ? (
        <Box sx={{ position: 'relative', height: 440 }}>
          <Loader lowPriority isLoading={isLoading} />
        </Box>
      ) : (
        <>
          {!data || data.length === 0 ? (
            placeholder
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 440, overflow: 'auto', width: '100%' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns.map((column, index) => (
                      <TableCell key={index}>{column.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((item, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {columns.map((column, colIndex) => (
                        <TableCell key={colIndex}>{column.render(item)}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter sx={{ position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 2 }}>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={perPageOptions}
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      page={page - 1}
                      onPageChange={(_, newPage) => onPageChange(newPage + 1)}
                      onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};
